import { Accessor, createSignal, onCleanup, onMount } from 'solid-js';

type InterceptedMatchPayload = {
  matchId: string | null;
  data: any;
  url: string;
};

type SportradarBridgeConfig = {
  enabled: boolean;
  targetOrigin: string;
};

type LivematchBridgeMessage = {
  source?: string;
  type?: string;
  payload?: InterceptedMatchPayload;
};

export const [timelineDeltaData, setTimelineDeltaData] = createSignal<any>(null);
export const [timelineData, setTimelineData] = createSignal<any>(null);
export const [cricketData, setCricketData] = createSignal<InterceptedMatchPayload | null>(null);

let bridgeConfig: SportradarBridgeConfig = {
  enabled: true,
  targetOrigin: '*',
};

const normalizeMatchId = (value: unknown) => {
  if (value == null) return null;

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  return rawValue.match(/(\d+)\s*$/)?.[1] ?? rawValue;
};

const extractMatchIdFromUrl = (urlObj: URL) => {
  const paramKeys = ['matchId', 'match_id', 'eventId', 'event_id', 'id'];

  for (const key of paramKeys) {
    const value = normalizeMatchId(urlObj.searchParams.get(key));
    if (value) return value;
  }

  const path = urlObj.pathname;
  const pathPatterns = [
    /\/match_timelinedelta\/(\d+)/,
    /\/match_timeline\/(\d+)/,
    /\/get_scorecard\/(\d+)/,
    /\/match\/(\d+)/,
  ];

  for (const pattern of pathPatterns) {
    const matched = path.match(pattern)?.[1];
    if (matched) return matched;
  }

  return normalizeMatchId(path);
};

const extractMatchIdFromPayload = (data: any) => {
  const score = data?.doc?.[0]?.data?.score;
  const candidates = [
    score?.matchId,
    score?.matchid,
    score?.match?._id,
    score?.match?.id,
    score?.match?.matchId,
    data?.doc?.[0]?.data?.match?._id,
    data?.doc?.[0]?.data?.match?.id,
  ];

  for (const candidate of candidates) {
    const matchId = normalizeMatchId(candidate);
    if (matchId) return matchId;
  }

  return null;
};

const postInterceptedDataToParent = (type: string, payload: InterceptedMatchPayload) => {
  if (!bridgeConfig.enabled) return;
  if (typeof window === 'undefined' || window.parent === window) return;

  window.parent.postMessage(
    {
      source: 'bz-sports',
      type,
      payload,
    },
    bridgeConfig.targetOrigin
  );
};

function setupSportradarDataInterceptor() {
  if (typeof window === 'undefined') return;
  if ((window as any).__timeline_intercepted) return;
  (window as any).__timeline_intercepted = true;

  const handleInterceptedData = (urlStr: string, data: any) => {
    try {
      const urlObj = new URL(urlStr, window.location.origin);
      if (urlObj.hostname !== 'lmt.fn.sportradar.com') return;

      const path = urlObj.pathname;
      if (/\/match_timelinedelta\//.test(path)) {
        setTimelineDeltaData(data);
        postInterceptedDataToParent('BZ_LIVEMATCH_TIMELINE_DELTA', {
          matchId: extractMatchIdFromPayload(data) ?? extractMatchIdFromUrl(urlObj),
          data,
          url: urlStr,
        });
      } else if (/\/cricket\/get_scorecard\//.test(path)) {
        const payload = {
          matchId: extractMatchIdFromPayload(data) ?? extractMatchIdFromUrl(urlObj),
          data,
          url: urlStr,
        };
        setCricketData(payload);
        postInterceptedDataToParent('BZ_LIVEMATCH_CRICKET_SCORECARD', payload);
      } else if (/\/match_timeline\//.test(path)) {
        setTimelineData(data);
        postInterceptedDataToParent('BZ_LIVEMATCH_TIMELINE', {
          matchId: extractMatchIdFromPayload(data) ?? extractMatchIdFromUrl(urlObj),
          data,
          url: urlStr,
        });
      }
    } catch {}
  };

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as any)?.url || String(args[0]);
      if (url) {
        const clone = response.clone();
        clone
          .json()
          .then((data) => {
            handleInterceptedData(url, data);
          })
          .catch(() => {});
      }
    } catch {}
    return response;
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: any[]) {
    (this as any)._m_url = String(url);
    // @ts-expect-error override
    return originalXhrOpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    this.addEventListener('load', function () {
      try {
        const urlStr = (this as any)._m_url;
        if (urlStr) {
          const data = JSON.parse(this.responseText);
          handleInterceptedData(urlStr, data);
        }
      } catch {}
    });
    return originalXhrSend.call(this, body);
  };
}

export function initSportradarDataInterceptor() {
  if (import.meta.env.SSR) return;
  setupSportradarDataInterceptor();
}

export function configureSportradarDataBridge(config: Partial<SportradarBridgeConfig>) {
  bridgeConfig = {
    ...bridgeConfig,
    ...config,
  };
}

export function useSportradarPostMessageBridge(
  matchId: Accessor<string | null | undefined>,
  options?: {
    expectedOrigin?: Accessor<string | undefined>;
  }
) {
  const [hasBridgeData, setHasBridgeData] = createSignal(false);

  onMount(() => {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent<LivematchBridgeMessage>) => {
      const message = event.data;
      if (message?.source !== 'bz-sports') return;
      const expectedOrigin = options?.expectedOrigin?.();
      if (expectedOrigin && event.origin !== expectedOrigin) return;

      const payload = message.payload;
      if (!payload?.data) return;

      const activeMatchId = normalizeMatchId(matchId());
      const payloadMatchId = normalizeMatchId(payload.matchId);
      if (!activeMatchId || !payloadMatchId || payloadMatchId !== activeMatchId) return;

      if (message.type === 'BZ_LIVEMATCH_TIMELINE_DELTA') {
        setTimelineDeltaData(payload.data);
        setHasBridgeData(true);
      } else if (message.type === 'BZ_LIVEMATCH_TIMELINE') {
        setTimelineData(payload.data);
        setHasBridgeData(true);
      } else if (message.type === 'BZ_LIVEMATCH_CRICKET_SCORECARD') {
        setCricketData(payload);
        setHasBridgeData(true);
      }
    };

    window.addEventListener('message', handleMessage);
    onCleanup(() => window.removeEventListener('message', handleMessage));
  });

  return {
    hasBridgeData,
  };
}

export function useSportraderData() {
  onMount(() => {
    initSportradarDataInterceptor();
  });

  return {
    timelineDeltaData,
    cricketData,
    setTimelineDeltaData,
    setCricketData,
    timelineData,
    setTimelineData,
  };
}
